<template>
  <div>
    <ClientOnly>
      <div v-if="isLessonContext" class="mt-8 flex flex-wrap justify-end gap-4">
        <template v-if="isLessonEditing">
          <BaseTheButtonNew
            type="secondary--disabled"
            icon="close"
            icon-position="left"
            text="Отмена"
            is-small
            @click="cancelEditing"
          />
          <BaseTheButtonNew
            type="secondary"
            icon="tick"
            icon-position="left"
            text="Сохранить"
            is-small
            :is-disabled="isSaving"
            @click="saveSelection"
          />
        </template>
        <template v-else>
          <BaseTheButtonNew
            v-if="!isLessonDraft"
            type="secondary"
            icon="arrow-right"
            text="К уроку"
            is-small
            :href="`/teacher/lessons/${lessonId}`"
          />
          <BaseTheButtonNew
            type="secondary"
            icon="edit"
            icon-position="left"
            :text="editMaterialsText"
            is-small
            @click="startEditing"
          />
        </template>
      </div>
    </ClientOnly>

    <CommonTabs
      v-if="isMobile"
      :list="tabsList"
      :initial-tab-index="activeTabIndex"
      :extra-query="tabsExtraQuery"
      is-collection
      class="mb-8"
      @on-change="handleActiveTab"
    />
    <div v-else class="my-12 flex justify-between">
      <CommonTabs
        :list="tabsList"
        :initial-tab-index="activeTabIndex"
        :extra-query="tabsExtraQuery"
        is-collection
      />
      <TeacherTileToggler
        class="ml-10 hidden h-fit lg:flex"
        :active-tile="activeIndexTile"
        :icons="groupList"
        @change-group="handleActiveTile"
      />
    </div>
    <div v-if="isPresentation && category?.shelf_id !== 7" class="mb-4">
      <div
        class="rounded-md bg-[#A100090F] p-5 text-[16px] leading-[130%] shadow-drop-2"
      >
        Проект «Учителю и ученику» представляет вашему вниманию уникальную
        разработку – материал для каждого параграфа учебника по истории в
        формате интерактивной презентации. В данное время размещены тестовые
        версии. Будем рады Вашим комментариям и предложениям, направленным на
        адрес электронной почты article@histrf.ru.
      </div>
    </div>
    <div
      v-if="sortedCollection.length"
      class="wrap-group"
      :class="[
        groupClass,
        {
          'rounded-xl border-b border-[#F3F3F3] shadow-drop-2':
            groupClass !== 'tile' && !isDates,
        },
        { '-m-2.5': groupClass === 'tile' },
      ]"
    >
      <template
        v-for="(card, index) in sortedCollection"
        :key="`${card.id}_${index}`"
      >
        <Component
          :is="getCardComponent(card)"
          v-bind="getAttributes(card)"
          class="group-card"
          :class="[
            { 'border-b border-[#F3F3F3]': groupClass !== 'tile' },
            { 'books-card': isBooks },
            { date: isDates },
            { relative: isLessonEditing },
            { 'cursor-pointer': isLessonEditing },
          ]"
          @click="handleCardClick(card)"
        >
          <div
            class="relative rounded-xl"
            :class="{
              'pointer-events-none': isLessonEditing,
              'border border-collection':
                isLessonEditing && isMaterialSelected(card),
            }"
          >
            <div
              v-if="isLessonEditing && isMaterialSelected(card)"
              class="absolute -right-3 -top-3 z-10 grid size-8 place-content-center rounded-full bg-collection text-white-100"
            >
              <SvgIcon name="tick" class="size-4" />
            </div>
            <Component
              :is="isComponent"
              :data="card"
              :category-title="category.title"
              :scool-class="category.school_class"
              :shelf-name="category.shelf?.name"
              :paragraph-number="highlight.number"
              disable-announce
              @click:presentation="showPresentation(card)"
            />
          </div>
        </Component>
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  LESSON_DRAFT_ID,
  LESSON_QUERY_KEY,
  TEACHER_HIDDEN_CONTENT_TYPES_BY_SUBJECT,
} from '@/dictionary';

const NuxtLink = resolveComponent('NuxtLink');
const DateCard = resolveComponent('MainPageCardsReworkedTeacherDateCard');
const BooksCard = resolveComponent('MainPageCardsReworkedTeacherBooksCard');
const ContentCard = resolveComponent('MainPageCardsReworkedTeacherContentCard');
const ContentList = resolveComponent('MainPageCardsReworkedTeacherContentList');
const PresentationCard = resolveComponent(
  'MainPageCardsReworkedTeacherPresentationCard',
);

definePageMeta({
  layout: 'marginless',
});

const props = defineProps({
  highlight: {
    type: Object,
    default: () => ({}),
  },
  data: {
    type: Array,
    default: () => [],
  },
  category: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const router = useRouter();
const modalsStore = useModalsStore();
const { withLesson } = useLessonQuery();
const { isMobile, getScreenBreakpoint, isHorizontalPadOrMore } =
  useBreakpoint();

/**
 * Страница открыта из урока: в просмотре показываем только материалы урока,
 * а в режиме правки — все доступные, с отметками выбранных.
 */
const {
  lessonId,
  isLessonContext,
  isEditing: isLessonEditing,
  isSaving,
  isMaterialSelected,
  toggleMaterial,
  hasSelectedMaterials,
  getSelectedCount,
  startEditing,
  cancelEditing,
  saveSelection,
} = useLessonSelection(
  computed(() => ({ subject: props.category, paragraph: props.highlight })),
);

// у несохранённого урока нечего смотреть: материалы уходят на бэк только по «Сохранить»
const isLessonDraft = computed(() => lessonId.value === LESSON_DRAFT_ID);

// пока в параграф ничего не добавлено
const editMaterialsText = computed(() =>
  hasSelectedMaterials.value ? 'Редактировать материалы' : 'Добавить материалы',
);

const activeTab = ref(null);

// вкладки, скрытые в конкретном учебнике
const hiddenContentTypes = computed(
  () => TEACHER_HIDDEN_CONTENT_TYPES_BY_SUBJECT[props.category?.id] ?? [],
);

const excludedContentTypes = computed(() => [
  'biography',
  'document',
  // 'document_collection',
  'illustration',
  'region_heraldic',
  'region_route',
  'region_article',
  ...(!isMoscowTimeReached(2025, 10, 5, 8, 0) && props.category?.shelf_id !== 7
    ? ['presentation']
    : props.category?.shelf_id === 7
    ? ['audiomaterial', 'date']
    : []),
  ...hiddenContentTypes.value,
]);

const isWiki = computed(() => {
  return route.query.content === 'wiki';
});

const groupList = computed(() => {
  if (isWiki.value || isDates.value) return ['list'];
  return ['tile', 'list'];
});

const activeTile = computed(() => {
  if (isWiki.value || isDates.value) return 'list';
  return route.query.group || 'tile';
});

const activeIndexTile = computed(() => {
  return groupList.value?.findIndex((g) => g === activeTile.value);
});

const groupClass = computed(() => {
  if (isWiki.value || isDates.value) return 'list';
  if (['xs', 'sm', 'md'].includes(getScreenBreakpoint.value)) return 'tile';
  return groupList.value[activeIndexTile.value];
});

const isBooks = computed(() => {
  return route.query.content === 'book';
});

const isDates = computed(() => {
  return route.query.content === 'date';
});

const isPresentation = computed(() => {
  return route.query.content === 'presentation';
});

const isComponent = computed(() => {
  if (isPresentation.value && groupClass.value === 'tile')
    return PresentationCard;
  if (isDates.value) return DateCard;
  if (isWiki.value) return ContentList;
  if (isBooks.value && groupClass.value === 'tile') return BooksCard;
  if (!isBooks.value && groupClass.value === 'tile') return ContentCard;
  return ContentList;
});

// при просмотре урока в параграфе видны только его материалы
const isLessonPreview = computed(
  () => isLessonContext.value && !isLessonEditing.value,
);

const sortedCollection = computed(() => {
  const collection = props.data ?? [];

  if (!isLessonPreview.value) return collection;

  return collection.filter((card) => isMaterialSelected(card));
});

const contentTypes = computed(() => {
  return props.highlight?.content_types?.filter(
    ({ model }) => !excludedContentTypes.value.includes(model),
  );
});

// счётчик вкладки всегда равен числу показанных карточек
const tabsList = computed(() => {
  if (!isLessonPreview.value) return contentTypes.value;

  return contentTypes.value?.map((type) => ({
    ...type,
    count: getSelectedCount(type.model),
  }));
});

const tabsExtraQuery = computed(() => {
  if (!isLessonContext.value) return {};

  return { [LESSON_QUERY_KEY]: route.query[LESSON_QUERY_KEY] };
});

const activeTabIndex = computed(() => {
  const idx = contentTypes.value?.findIndex(
    ({ model }) => model === getModelType(route.query.content),
  );

  if (idx !== -1) return idx;
  const availableIdx = contentTypes.value?.findIndex(({ count }) => count > 0);

  if (availableIdx !== -1) return availableIdx;
  return 0;
});

/**methods */
const getCardComponent = (card) => {
  if (isLessonEditing.value || isPresentation.value) return 'div';
  if ((card?.outer_link || card?.url) && !isBooks.value) return 'a';
  return NuxtLink;
};

const handleCardClick = (card) => {
  toggleMaterial(card);
};

const getAttributes = (card) => {
  if (isDates.value || isLessonEditing.value) return {};
  return {
    ...(isBooks.value && { to: getBookUrl(card) }),
    ...(!isBooks.value &&
      !(card?.outer_link || card?.url) && { to: getUrl(card) }),
    ...((card?.outer_link || card?.url) &&
      !isBooks.value && { href: getUrl(card) }),
    ...(!isBooks.value && card?.outer_link && { target: '_blank' }),
  };
};

const handleActiveTile = (idx) => {
  if (idx === activeIndexTile.value) return;

  router.replace({
    query: {
      ...route.query,
      group: idx ? groupList.value[idx] : undefined,
    },
  });
};

const getModelType = (modelType) => {
  let itemModel = '';
  switch (modelType) {
    case 'audiolecture':
    case 'audiomaterial':
      itemModel = 'audiomaterial';
      break;
    case 'videolecture':
    case 'videomaterial':
      itemModel = 'videomaterial';
      break;
    default:
      itemModel = modelType;
      break;
  }
  return itemModel;
};

const handleActiveTab = (tab) => {
  if (tab?.model && route.query.content !== tab?.model) {
    router.replace({
      query: {
        ...route.query,
        content: tab.model,
      },
    });
  }

  activeTab.value = tab;
};

// withLesson только на внутренних ссылках
const getUrl = (card) => {
  return (
    card?.outer_link ??
    card?.url ??
    withLesson(
      `/teacher/${route.params.parent}/${route.params.category}/${
        card.model_type
      }/${card.slug || card.id}?content=${getModelType(
        card.model_type,
      )}${modernIsGroup(groupClass.value)}`,
    )
  );
};

const getBookUrl = (card) => {
  return withLesson(
    `/teacher/${route.params.parent}/${route.params.category}/${
      card.model_type
    }/${card.slug}?content=${getModelType(card.model_type)}${modernIsGroup(
      groupClass.value,
    )}`,
  );
};

const showPresentation = (card) => {
  if (isLessonEditing.value) return;

  isHorizontalPadOrMore.value && document.documentElement.requestFullscreen();
  modalsStore.setPropertiesModal({
    slides: card.slides,
    fullscreen: isHorizontalPadOrMore.value,
  });
  modalsStore.showGalleryPopup();
};

watch(
  () => route.query.content,
  () => {
    if (route.query?.content) {
      const queryTab = contentTypes.value?.find(
        ({ model }) => model === getModelType(route.query.content),
      );
      handleActiveTab(queryTab);
    } else {
      handleActiveTab(props.highlight?.content_types?.[0]);
    }
  },
  { immediate: true },
);

useHead(
  computed(() => ({
    title: `${
      props.highlight?.title ? props.highlight?.title : 'История РФ'
    } | Материалы по истории для школьного курса «История России» и «Всеобщая история» с 5 по 11 класс`,
    meta: [
      {
        name: 'description',
        content:
          'История Российской Федерации в статьях. Освещаем все вехи российской истории в статьях. Российское военно-историческое общество. Мы знаем правду.',
      },
    ],
  })),
);
</script>

<style lang="scss">
.wrap-group {
  @apply flex flex-wrap;

  .group-card {
    @apply w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/5 p-2.5;
  }
  .group-card.date {
    @apply p-0;
  }
  .group-card.books-card {
    @apply w-full lg:w-1/2 2xl:w-1/3 p-2.5;
  }

  &.list {
    @apply flex-col;

    .group-card {
      @apply w-full;
    }
  }
}
</style>
